import api from './api';

// =============================================
// Portal Types
// =============================================

export interface PortalDokument {
  portalDokumentID: number;
  titel: string;
  beschreibung?: string;
  kategorie: string;
  status: string;
  dateiName: string;
  dateiGroesse: number;
  mimeType: string;
  freigegebenAm?: string;
  hochgeladenAm?: string;
  vomPortalHochgeladen: boolean;
}

export interface PortalTicket {
  ticketID: number;
  titel: string;
  status: string;
  prioritaet?: string;
  erstelltAm: string;
  letzteAktualisierung?: string;
  statusBeschreibung?: string;
}

export interface PortalTicketDetail extends PortalTicket {
  beschreibung?: string;
  oeffentlicheKommentare: PortalTicketKommentar[];
  dokumente: PortalDokument[];
  nachrichten: PortalNachricht[];
}

export interface CreatePortalTicketDto {
  titel: string;
  beschreibung?: string;
  prioritaet?: string;
  kategorie?: string;
}

export interface SendeTicketNachrichtDto {
  nachricht: string;
}

export interface PortalTicketKommentar {
  erstelltAm: string;
  inhalt: string;
  vomMitarbeiter: boolean;
}

export interface PortalNachricht {
  portalNachrichtID: number;
  betreff: string;
  inhalt: string;
  gesendetAm: string;
  vomPortalBenutzer: boolean;
  gelesen: boolean;
  gelesenAm?: string;
  absenderName?: string;
  ticketID?: number;
  ticketTitel?: string;
  portalDokumentID?: number;
  dokumentTitel?: string;
}

export interface PortalDashboard {
  anzahlDokumente: number;
  anzahlNeuesDokumente: number;
  anzahlOffeneTickets: number;
  anzahlUngeleseneNachrichten: number;
  letzteDokumente: PortalDokument[];
  letzteTicketUpdates: PortalTicket[];
  letzteNachrichten: PortalNachricht[];
}

export interface SendeNachrichtDto {
  betreff: string;
  inhalt: string;
  ticketID?: number;
  portalDokumentID?: number;
}

// =============================================
// Portal API Service
// =============================================

export const portalApi = {
  // Dashboard
  getDashboard: () =>
    api.get<PortalDashboard>('/portal/dashboard'),

  // Dokumente
  getMeineDokumente: () =>
    api.get<PortalDokument[]>('/portal/dokumente'),

  getDokument: (id: number) =>
    api.get<PortalDokument>(`/portal/dokumente/${id}`),

  downloadDokument: (id: number) =>
    api.get(`/portal/dokumente/${id}/download`, { responseType: 'blob' }),

  uploadDokument: (file: File, titel: string, beschreibung?: string, kategorie: string = 'Beleg') => {
    const formData = new FormData();
    formData.append('datei', file);
    formData.append('titel', titel);
    if (beschreibung) formData.append('beschreibung', beschreibung);
    formData.append('kategorie', kategorie);
    return api.post<PortalDokument>('/portal/dokumente/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Tickets
  getMeineTickets: () =>
    api.get<PortalTicket[]>('/portal/tickets'),

  getTicketDetail: (id: number) =>
    api.get<PortalTicketDetail>(`/portal/tickets/${id}`),

  getTicketStatistik: () =>
    api.get<{ gesamt: number; offen: number; inBearbeitung: number; erledigt: number; letzteAktualisierung?: string }>('/portal/tickets/statistik'),

  createTicket: (dto: CreatePortalTicketDto) =>
    api.post<PortalTicket>('/portal/tickets', dto),

  getTicketNachrichten: (ticketId: number) =>
    api.get<PortalNachricht[]>(`/portal/tickets/${ticketId}/nachrichten`),

  sendeTicketNachricht: (ticketId: number, dto: SendeTicketNachrichtDto) =>
    api.post<PortalNachricht>(`/portal/tickets/${ticketId}/nachricht`, dto),

  // Nachrichten
  getNachrichten: () =>
    api.get<PortalNachricht[]>('/portal/nachrichten'),

  getUngeleseneAnzahl: () =>
    api.get<number>('/portal/nachrichten/ungelesen/anzahl'),

  sendeNachricht: (dto: SendeNachrichtDto) =>
    api.post<PortalNachricht>('/portal/nachrichten', dto),

  markiereGelesen: (id: number) =>
    api.put(`/portal/nachrichten/${id}/gelesen`),

  markiereAlleGelesen: () =>
    api.put<{ markedAsRead: number }>('/portal/nachrichten/alle-gelesen'),
};

// =============================================
// Admin API für Portal-Verwaltung (Mitarbeiter)
// =============================================

export interface DokumentFreigabeDto {
  dokumentID: number;
  adresseID: number;
  titel?: string;
  beschreibung?: string;
  kategorie: string;
}

export interface PortalEinladungDto {
  adresseID: number;
  email: string;
  benutzername?: string;
}

export interface PortalEinladungResult {
  success: boolean;
  benutzername?: string;
  tempPasswort?: string;
  errorMessage?: string;
}

export interface MandantAdresse {
  adresseID: number;
  firma?: string;
  vorname?: string;
  nachname?: string;
  email?: string;
  hatPortalZugang: boolean;
}

export interface AdminPortalDokument {
  portalDokumentID: number;
  adresseID: number;
  adresseName?: string;
  titel: string;
  beschreibung?: string;
  kategorie: string;
  status: string;
  dateiName: string;
  dateiGroesse: number;
  vomPortalHochgeladen: boolean;
  hochgeladenAm?: string;
  freigegebenAm?: string;
}

export interface AdminKonversation {
  adresseID: number;
  adresseName: string;
  anzahlNachrichten: number;
  anzahlUngelesen: number;
  letzteNachrichtAm: string;
  letzterBetreff: string;
  letzteNachrichtVomPortal: boolean;
}

export interface AdminPortalNachricht {
  portalNachrichtID: number;
  betreff: string;
  inhalt: string;
  gesendetAm: string;
  vomPortalBenutzer: boolean;
  gelesen: boolean;
  adresseID: number;
  adresseName?: string;
  ticketID?: number;
  absenderName?: string;
}

export interface AntwortAnPortalDto {
  adresseID: number;
  betreff: string;
  inhalt: string;
  ticketID?: number;
  portalDokumentID?: number;
}

export const portalAdminApi = {
  // Dokument für Portal freigeben
  dokumentFreigeben: (dto: DokumentFreigabeDto) =>
    api.post<PortalDokument>('/portal/dokument-freigeben', dto),

  // Portal-Benutzer einladen
  benutzerEinladen: (dto: PortalEinladungDto) =>
    api.post<PortalEinladungResult>('/portal/einladen', dto),

  // Liste der Mandanten (für Dropdown)
  getMandanten: () =>
    api.get<MandantAdresse[]>('/portal/mandanten'),

  // Alle Portal-Dokumente (für Admin-Übersicht)
  getAlleDokumente: (options?: { status?: string; nurHochgeladen?: boolean }) =>
    api.get<AdminPortalDokument[]>('/portal/admin/dokumente', { params: options }),

  // Portal-Dokument herunterladen (Admin)
  downloadDokument: (id: number) =>
    api.get(`/portal/admin/dokumente/${id}/download`, { responseType: 'blob' }),

  // Portal-Dokument Status ändern
  updateDokumentStatus: (id: number, status: string) =>
    api.put(`/portal/admin/dokumente/${id}/status`, { status }),

  // Freigegebene Dokumente für eine Adresse
  getFreigegebeneDokumente: (adresseId: number) =>
    api.get<PortalDokument[]>(`/portal/admin/dokumente/${adresseId}`),

  // ===== Nachrichten-Verwaltung =====

  // Alle Konversationen (gruppiert nach Adresse)
  getKonversationen: () =>
    api.get<AdminKonversation[]>('/portal/admin/nachrichten'),

  // Nachrichten-Verlauf für eine Adresse
  getKonversation: (adresseId: number) =>
    api.get<AdminPortalNachricht[]>(`/portal/admin/nachrichten/${adresseId}`),

  // Anzahl ungelesener Nachrichten
  getUngeleseneAnzahl: () =>
    api.get<number>('/portal/admin/nachrichten/ungelesen/anzahl'),

  // Antwort an Portal-Benutzer senden
  antwortSenden: (dto: AntwortAnPortalDto) =>
    api.post<AdminPortalNachricht>('/portal/nachrichten/antworten', dto),
};

export default portalApi;
